import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import IntervalInput, {
  parseIntervalInput,
  intervalToLatex,
} from "../../../../components/inputs/IntervalInput";
import {
  parseRootFractionInput,
  formatLatexPreview as formatNumLatex,
} from "../../../../components/inputs/RootFractionInput";
import FractionInput, {
  formatLatexPreview as formatFracPreview,
} from "../../../../components/inputs/FractionInput";

/* ========= helpers ========= */
const coeffX = (a) => (a === 1 ? "x" : a === -1 ? "-x" : `${a}x`);
const withSign = (n) => (n >= 0 ? `+ ${n}` : `- ${Math.abs(n)}`);
const toLatexNumber = (raw) => (formatNumLatex(String(raw), "") || String(raw));

const gcd = (a, b) => { a = Math.abs(a); b = Math.abs(b);
  while (b) { const t = a % b; a = b; b = t; }
  return a || 1;
};
function simplifyFrac(num, den) {
  if (den < 0) { num = -num; den = -den; }
  const g = gcd(num, den);
  return { num: num / g, den: den / g };
}
function improperLatexFromFrac(num, den) {
  const { num: N, den: D } = simplifyFrac(num, den);
  if (N % D === 0) return String(N / D);
  const sign = N < 0 ? "-" : "";
  return `${sign}\\dfrac{${Math.abs(N)}}{${D}}`;
}
function mixedLatexFromFrac(num, den) {
  const { num: N, den: D } = simplifyFrac(num, den);
  if (N % D === 0) return null;
  const A = Math.floor(Math.abs(N) / D);
  const R = Math.abs(N) % D;
  if (A === 0) return null;
  const sign = N < 0 ? "-" : "";
  return `${sign}${A}\\;\\dfrac{${R}}{${D}}`;
}
function finalPointLatex(num, den) {
  return mixedLatexFromFrac(num, den) || improperLatexFromFrac(num, den);
}

/* ========= generator ========= */
export function generateTask() {
  const pick = (min, max) =>
    (Math.random() < 0.5 ? -1 : 1) * (Math.floor(Math.random() * (max - min + 1)) + min);

  let a, b, c, d, e, f, g, h;
  while (true) {
    c = pick(1, 4);
    g = pick(1, 4);
    a = pick(1, 6);
    b = pick(1, 9);
    d = pick(1, 9);
    f = pick(1, 9);
    h = pick(1, 9);

    if ((a * g) % c !== 0) continue;
    e = (a * g) / c;
    if (Math.abs(e) > 9 || e === 0) continue;

    const x0 = simplifyFrac(-d, c);
    const x1 = simplifyFrac(-h, g);
    if (x0.num * x1.den === x1.num * x0.den) continue;

    const L = a * h + b * g - e * d - f * c;
    const R = f * d - b * h;
    if (L === 0) continue;

    const xr = simplifyFrac(R, L);

    return {
      type: "RationalTwoFracsSolveAndDomainTask",
      data: {
        a, b, c, d, e, f, g, h,
        latex: `\\dfrac{${coeffX(a)} ${withSign(b)}}{${coeffX(c)} ${withSign(d)}} = \\dfrac{${coeffX(e)} ${withSign(f)}}{${coeffX(g)} ${withSign(h)}}`,
        x0_num: x0.num, x0_den: x0.den, x0_val: x0.num / x0.den,
        x1_num: x1.num, x1_den: x1.den, x1_val: x1.num / x1.den,
        L, R,
        xr_num: xr.num, xr_den: xr.den, xr_val: xr.num / xr.den,
        hasSolution: true,
      },
    };
  }
}

/* ========= normalizacja tekstów od użytkownika ========= */
function normalizeUserNumberText(txt) {
  // zamień egzotyczne minusy i spacje na zwykłe
  return String(txt ?? "")
    .replace(/[−–—]/g, "-")
    .replace(/\u00A0|\u202F|\u2009|\u200A|\u200B/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/* ========= parse R\{a,b} | R-{a,b} | ℝ-{a,b} | x∈R\{...} ========= */
function parseComplementSet(raw) {
  if (!raw) return null;

  let str = normalizeUserNumberText(raw);

  // normalizacja "ℝ" → "R"
  str = str.replace(/ℝ|ℜ|ℝ/g, "R");

  // usuń prefix typu "D:" lub "x ∈"
  str = str.replace(/^D[:=]?\s*/i, "");
  str = str.replace(/^x\s*∈\s*/i, "");

  // zamiana myślników na zwykły minus
  str = str.replace(/[−–—]/g, "-");

  // usunięcie podwójnych minusów / separatorów
  str = str.replace(/\s*-\s*/g, "-");

  // match operatora pomiędzy R a {...}
  // łapie: R\{a,b}, R/{a,b}, R-{a,b}
  const m = str.match(
    /R\s*(?:[\\\/\-])\s*{([^}]*)}/i
  );

  if (!m) return null;

  // wnętrze klamry "a, b"
  const insideRaw = m[1];
  if (!insideRaw) return null;

  const items = insideRaw
    .split(/[;,]/)
    .map(s => normalizeUserNumberText(s))
    .filter(Boolean);

  if (!items.length) return null;

  // parsowanie wartości liczbowych – ułamki, mieszane itp.
  const vals = items
    .map(s => parseRootFractionInput(s.replace(",", ".")))
    .filter(v => Number.isFinite(v));

  return vals.length ? vals : null;
}


/* ========= LIVE preview dziedziny (działa też, gdy klamra/nawias nie jest domknięta) ========= */
function liveDomainPreviewLatex(raw) {
  const str = normalizeUserNumberText(raw);
  if (!str) return "";
  try {
    const t = intervalToLatex(str, { withX: true });
    if (t && /\S/.test(t)) return `D\\;:\\; ${t}`;
  } catch (_) {}
  const m = str.match(/[Rℝ]\s*\\\s*{([^}]*)?$/i);
  if (m) {
    const rawInside = m[1] ?? "";
    const parts = rawInside.split(/[;,]/).map(s => normalizeUserNumberText(s)).filter(Boolean);
    const pretty = parts.map(s => {
      const nice = formatNumLatex(String(s).replace(",", "."), "");
      return (nice && /\S/.test(nice)) ? nice : String(s);
    });
    const body = pretty.join(",\\,");
    return `D\\;:\\; \\mathbb{R}\\setminus\\{${body}\\}`;
  }
  return `D\\;:\\; \\text{${str.replace(/\\/g, "\\\\")}}`;
}

/* ========= zadanie ========= */
export function renderTask({ data }) {
  return <p>Podaj dziedzinę i rozwiąż równanie: <InlineMath math={data.latex} /></p>;
}

/* ========= input ========= */
export function renderInput(_, userInput = {}, onChange, disabled) {
  const rawD = typeof userInput?.D === "string" ? userInput.D : (userInput?.D?.raw || "");
  const dLatexLive = liveDomainPreviewLatex(rawD);

  const xVal = userInput?.x;

  return (
    <div className="flex flex-col gap-4">
      {/* D: */}
      <div className="flex flex-col gap-1">
        <div style={{ lineHeight: 1, marginBottom: 2 }}>
          <InlineMath math={'D\\;:'} />
        </div>

        <IntervalInput
          value={userInput?.D || ""}
          onChange={(val) => onChange({ ...userInput, D: val })}
          disabled={disabled}
          asString
          showPreview={false}
          placeholder="np. ℝ\{3}  lub  ℝ\{3, 5}  lub  ℝ - {3}"
          className="interval-no-preview"
        />

        {rawD ? (
          <div style={{ pointerEvents: "none", marginTop: 2 }}>
            <InlineMath math={`\\displaystyle ${dLatexLive}`} />
          </div>
        ) : null}
      </div>

      {/* x = ... */}
      <div className="flex flex-col gap-1">
        <div style={{ lineHeight: 1 }}>
          <InlineMath math={'x\\;='} />
        </div>

        <FractionInput
          value={typeof xVal === "object" ? xVal : (typeof xVal === "string" ? xVal : "")}
          onChange={(val) => onChange({ ...userInput, x: val })}
          disabled={disabled}
          placeholder="np. 2, 3/5, 1 2/3 lub brak"
          label="x"
        />
      </div>
    </div>
  );
}

/* ========= check ========= */
function matchesNoneKeyword(s) {
  if (!s) return false;
  const t = normalizeUserNumberText(s).toLowerCase();

  // usuwamy polskie znaki tylko do dopasowania fraz typu "rozwiazan", "zbior"
  const noDia = t
    .replace(/ą/g, "a").replace(/ć/g, "c").replace(/ę/g, "e")
    .replace(/ł/g, "l").replace(/ń/g, "n").replace(/ó/g, "o")
    .replace(/ś/g, "s").replace(/ż/g, "z").replace(/ź/g, "z");

  return (
    t === "∅" ||
    t === "ø" || t === "Ø" ||
    noDia === "brak" ||
    noDia === "brak odpowiedzi" ||
    noDia === "brak rozwiazan" ||
    noDia === "pusty" ||
    noDia === "zbior pusty" ||
    t === "empty" ||
    t === "none" ||
    /^\{\s*\}$/.test(t)
  );
}

export function checkAnswer({ data }, userInput = {}) {
  const tol = 0.01 + 1e-9;
  const eq = (x, y) => Math.abs(x - y) <= tol;
  const x0 = data.x0_val, x1 = data.x1_val;
  const [p, q] = x0 < x1 ? [x0, x1] : [x1, x0];

  // --- dziedzina ---
  const rawD = typeof userInput?.D === "string" ? userInput.D : (userInput?.D?.raw || "");
  let domainOK = false;

  // (A) ℝ\{a,b} – akceptuj dowolną kolejność, bo poniżej sortujemy
  const setVals = parseComplementSet(rawD);
  if (setVals && setVals.length >= 2) {
    const got = setVals.sort((u,v)=>u-v);
    domainOK = eq(got[0], p) && eq(got[1], q);
  }

  // (B) suma trzech przedziałów
  if (!domainOK) {
    const parsed = parseIntervalInput(normalizeUserNumberText(rawD));
    if (parsed && parsed.kind === "intervals") {
      const ints = parsed.intervals;
      if (ints.length === 3) {
        const ends = [];
        for (const I of ints) {
          if (Number.isFinite(I.left))  ends.push({v:I.left, side:"L", open:!I.leftClosed});
          if (Number.isFinite(I.right)) ends.push({v:I.right, side:"R", open:!I.rightClosed});
        }
        const uniq = [];
        for (const e of ends) {
          if (!uniq.some(u=>eq(u.v,e.v))) uniq.push(e);
        }
        if (uniq.length === 2 && uniq.every(u=>u.open)) {
          const vs = uniq.map(u=>u.v).sort((u,v)=>u-v);
          domainOK = eq(vs[0], p) && eq(vs[1], q);
        }
      }
    }
  }

  // --- rozwiązanie ---
  const xr = data.xr_val;
  const hasSolution = !(eq(xr, x0) || eq(xr, x1));

  const xVal = userInput?.x;
  const rawString = typeof xVal === "string" ? xVal : (typeof xVal === "object" ? xVal?.raw : "");

  // akceptuj słowa-klucze i symbole dla "braku"
  const wantNoneKeyword = matchesNoneKeyword(rawString);

  // puste pole też jest OK, jeśli rzeczywiście brak rozwiązań
  const isEmptyObj = (typeof xVal === "object" && !xVal?.raw);
  const isEmptyStr = (typeof xVal === "string" && normalizeUserNumberText(xVal) === "");
  const wantNone = wantNoneKeyword || (!hasSolution && (isEmptyObj || isEmptyStr));

  // liczba z FractionInput (parsed) lub string (legacy)
  let parsedX = NaN;
  if (typeof xVal === "object" && Number.isFinite(xVal?.parsed)) {
    parsedX = xVal.parsed;
  } else if (typeof xVal === "string") {
    parsedX = parseRootFractionInput(normalizeUserNumberText(rawString).replace(",", "."));
  } else if (typeof xVal === "number") {
    parsedX = xVal;
  }

  let solutionOK = false;
  if (!hasSolution) {
    solutionOK = wantNone;
  } else {
    if (Number.isFinite(parsedX)) solutionOK = eq(parsedX, xr);
  }

  return {
    correct: domainOK && solutionOK,
    expected: { D: [x0, x1], x: hasSolution ? xr : null, hasSolution },
  };
}

/* ========= short answer ========= */
export function renderAnswer({ data }) {
  // kanoniczny porządek – mniejsza, większa
  const x0L = finalPointLatex(data.x0_num, data.x0_den);
  const x1L = finalPointLatex(data.x1_num, data.x1_den);
  const xrL = finalPointLatex(data.xr_num, data.xr_den);
  const inDomain = data.xr_num * data.x0_den !== data.x0_num * data.xr_den &&
                   data.xr_num * data.x1_den !== data.x1_num * data.xr_den;

  return (
    <div style={{ textAlign: "left", marginTop: "0.5rem" }}>
      <div>
        <InlineMath math={`D:\\; x\\in\\mathbb{R}\\setminus\\{${x0L},\\,${x1L}\\}`} />
      </div>
      <div style={{ marginTop: "0.4rem" }}>
        {inDomain
          ? <InlineMath math={`x\\;=\\;${xrL}`} />
          : <i>Brak rozwiązań (wynik wypada z dziedziny).</i>}
      </div>
    </div>
  );
}

/* ========= user answer preview ========= */
export function renderUserAnswer(userInput = {}) {
  const rawD = typeof userInput === "string"
    ? userInput
    : (userInput?.D?.raw || userInput?.D || "");

  const dLatex = liveDomainPreviewLatex(rawD);

  // x — pokaż zawsze z „x = …” dla object/string/number
  const xObj = userInput?.x;
  let xLatex = "";
  if (typeof xObj === "object" && xObj?.raw) {
    xLatex = formatFracPreview(xObj.raw, "x"); // już w formie "x = …"
  } else if (typeof xObj === "string" && normalizeUserNumberText(xObj)) {
    const pretty = (formatNumLatex(String(xObj), "") || String(xObj)).trim();
    xLatex = `x\\;=\\;${pretty}`;
  } else if (typeof xObj === "number" && Number.isFinite(xObj)) {
    xLatex = `x\\;=\\;${toLatexNumber(xObj)}`;
  }

  return (
    <div className="space-y-1">
      {rawD ? <div><InlineMath math={`\\displaystyle ${dLatex}`} /></div> : null}
      {xLatex ? <div><InlineMath math={xLatex} /></div> : null}
      {!rawD && !xLatex ? <i>(brak odpowiedzi)</i> : null}
    </div>
  );
}

export function renderExplanation({ data }) {
  const { a,b,c,d,e,f,g,h, x0_num,x0_den, x1_num,x1_den, L, R, xr_num, xr_den } = data;

  const aL = toLatexNumber(a), bL = toLatexNumber(b),
        cL = toLatexNumber(c), dL = toLatexNumber(d),
        eL = toLatexNumber(e), fL = toLatexNumber(f),
        gL = toLatexNumber(g), hL = toLatexNumber(h);

  const ag   = a * g;
  const ec   = e * c;
  const AhBg = a * h + b * g;
  const EdFc = e * d + f * c;
  const Bh   = b * h;
  const Fd   = f * d;

  const x0Final = finalPointLatex(x0_num, x0_den);
  const x1Final = finalPointLatex(x1_num, x1_den);
  const xrFinal = finalPointLatex(xr_num, xr_den);

  const inDomain =
    xr_num * x0_den !== x0_num * xr_den &&
    xr_num * x1_den !== x1_num * xr_den;

  const opDivL =
    Math.abs(L) === 1 ? "" : (L < 0 ? `:\\,(${toLatexNumber(L)})` : `:\\,${toLatexNumber(L)}`);

  return (
    <div className="space-y-4">
      <p><strong>Krok 1:</strong> Warunki z mianowników i wykluczenie punktów.</p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={`${coeffX(c)} ${withSign(d)} \\neq 0 \\qquad ${coeffX(g)} ${withSign(h)} \\neq 0`} />
      </div>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={`x \\neq ${x0Final} \\qquad x \\neq ${x1Final}`} />
      </div>

      <p><strong>Krok 2:</strong> Wymnażamy na krzyż i rozwiązujemy.</p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={
          String.raw`
          (${aL}x ${withSign(b)})(${coeffX(g)} ${withSign(h)}) \;=\; (${eL}x ${withSign(f)})(${coeffX(c)} ${withSign(d)})
          `
        } />
      </div>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={
          String.raw`
          ${toLatexNumber(ag)}x^2\; ${withSign(AhBg)}x\; ${withSign(Bh)}
          \;=\;
          ${toLatexNumber(ec)}x^2\; ${withSign(EdFc)}x\; ${withSign(Fd)}
          `
        } />
      </div>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={
          String.raw`\begin{aligned}
            ${toLatexNumber(L)}\,x &= ${toLatexNumber(R)} ${opDivL ? `&\\Big|&\\ ${opDivL}` : ""} \\
            x &= ${xrFinal}
          \end{aligned}`
        } />
      </div>

      <p><strong>Krok 3:</strong> Odpowiedź — sprawdzamy, czy wynik należy do dziedziny: <InlineMath math={`x\\neq ${x0Final}\\;\\text{ i }\\;x\\neq ${x1Final}`} />.</p>

      <div style={{ textAlign: "center", marginBottom: "0.25rem" }}>
        <BlockMath math={`D\\;:\\;\\mathbb{R}\\setminus\\{${x0Final},\\,${x1Final}\\}`} />
      </div>

      <div style={{ textAlign: "center", marginTop: "-2px" }}>
        {inDomain
          ? <em>Należy do dziedziny, więc jest rozwiązaniem.</em>
          : <em>Nie należy do dziedziny, więc rozwiązań brak.</em>}
      </div>

      {inDomain ? (
        <div style={{ textAlign: "center" }}>
          <BlockMath math={`x = ${xrFinal}`} />
        </div>
      ) : null}
    </div>
  );
}
